import { getLocationPartId } from './locationPartMapping';
import { formatPartLabel } from './partLabels';

/**
 * How component locations are named and ordered for a reader.
 *
 * The platform seeds bearing locations free-end first — `data/location_types.json` lists
 * `*_free_end` before `*_drive_end` for pump, compressor, mill, engine, shaft, turbine, crusher
 * and roll — and `_create_locations` numbers them L1, L2 in that order. So every one of those
 * families lists its free end first and calls it L1, which reads as the two ends being swapped.
 *
 * Renaming them is not on: L1/L2 are the names historical tags and reports were written
 * against, and renumbering would rewrite them on every asset ever built. What a reader actually
 * needs is the end SPELLED OUT and the drive end FIRST, which is a presentation choice and
 * costs nothing downstream.
 *
 * Both answers come from `getLocationPartId`, the classifier the twin already tints and binds
 * by, so a label here can never disagree with the pad it names.
 */

/** Rank for display: drive end, then free end, then anything else, then unclassifiable. */
const END_RANK = { drive_end: 0, rear_end: 1 };
const OTHER_RANK = 2;
const UNKNOWN_RANK = 3;

/*
 * Which shaft a location belongs to, so a multi-shaft body is grouped by shaft before being
 * ordered by end — otherwise a six-location multi-stage gearbox would list all three drive ends
 * and then all three free ends, which is less readable than what it replaced.
 *
 * Mirrors the pattern in MountPointManager.getLocationShaft, which is module-private there. Keep
 * the two in step; they answer the same question for the same vocabulary.
 */
const SHAFT_RE = /\b(input|idle|intermediate|output|main)\s+shaft\b/;

function normalize(location) {
  if (!location) return '';
  return [location.name, location.description, location.location_type_name]
    .filter(Boolean)
    .join(' ')
    .toLowerCase()
    .replace(/[_-]+/g, ' ');
}

function shaftOf(location) {
  const match = SHAFT_RE.exec(normalize(location));
  return match ? match[1] : '';
}

/*
 * Deliberately WITHOUT a model key. getLocationPartId takes one to switch the two multi-stage
 * gearboxes onto their per-shaft tagging, where every part of the input shaft answers
 * `drive_end` — correct for deciding which mesh to tint, and wrong for naming an end, because
 * it would call `input_shaft_free_end` the drive end. Naming and ordering want the end word.
 */
function rankOf(location) {
  const partId = getLocationPartId(location);
  if (!partId) return UNKNOWN_RANK;
  const rank = END_RANK[partId];
  return rank == null ? OTHER_RANK : rank;
}

/**
 * "Drive End" / "Free End" for a location, or null when its text does not say which end it is.
 * Callers fall back to the location's own name.
 */
export function getLocationEndLabel(location, t) {
  const partId = getLocationPartId(location);
  if (partId !== 'drive_end' && partId !== 'rear_end') return null;
  return formatPartLabel(partId, t);
}

/**
 * Locations ordered for display: grouped by shaft in the order the groups first appear, and
 * drive end before free end within each group. Stable everywhere else, so anything this cannot
 * classify keeps the order the payload delivered it in.
 */
export function sortLocationsForDisplay(locations) {
  const list = Array.isArray(locations) ? locations : [];
  const groupOrder = [];
  list.forEach((location) => {
    const shaft = shaftOf(location);
    if (groupOrder.indexOf(shaft) < 0) groupOrder.push(shaft);
  });

  return list
    .map((location, index) => ({ location, index }))
    .sort((left, right) => {
      const groupDelta = groupOrder.indexOf(shaftOf(left.location))
        - groupOrder.indexOf(shaftOf(right.location));
      if (groupDelta !== 0) return groupDelta;
      const rankDelta = rankOf(left.location) - rankOf(right.location);
      if (rankDelta !== 0) return rankDelta;
      // Array.prototype.sort is only guaranteed stable from ES2019; the build targets older
      // engines, so carry the original index rather than relying on it.
      return left.index - right.index;
    })
    .map(entry => entry.location);
}
